import { useState } from "react";
import { MapPin, Users, FileText, Building2, ExternalLink, Download, Pencil, Plus, Trash2, X, Save, Loader2, Settings2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import Combobox from "@/components/Combobox";
import DateField from "@/components/DateField";
import PPNBreakdown from "@/components/PPNBreakdown";
import { INDONESIA_REGIONS, PROVINCES } from "@/lib/indonesia";
import { toDateInput, toIso } from "@/components/project-dialog/utils";

/**
 * DataProyekTab — tab Data Proyek Dashboard Monitoring.
 * 2026-06-20 (user request): "Edit Data Project" TIDAK lagi membuka popup —
 * seluruh tampilan tab berubah menjadi form editable inline (Informasi, Lokasi,
 * Ruang Lingkup, Kontak, Dokumen, Pengaturan Kontrak) dengan tombol Simpan/Batal.
 * Untuk project bertipe VO/WO, edit tetap diarahkan ke dialog lama via onEdit
 * (field-nya berbeda dan dikelola di halaman WO/VO).
 */

const Row = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <div className="overline text-[9px] text-slate-500">{label}</div>
    <div className="text-xs text-[#0B1D3A] font-medium mt-0.5">{value ?? <span className="text-slate-300">—</span>}</div>
  </div>
);

const ERow = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <div className="overline text-[9px] text-slate-500 mb-1">{label}</div>
    {children}
  </div>
);

const Section = ({ icon: Icon, title, children, testid, right }) => (
  <section className="bg-white border border-slate-200" data-testid={testid}>
    <header className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2">
      <Icon size={14} className="text-[#0B1D3A]" />
      <h3 className="text-sm font-semibold text-[#0B1D3A]">{title}</h3>
      {right && <div className="ml-auto">{right}</div>}
    </header>
    <div className="p-4">{children}</div>
  </section>
);

const inp = "rounded-none h-8 text-xs";
const num = (v) => (v === "" || v == null ? null : parseFloat(v));

export default function DataProyekTab({ project, mon, canEdit, onEdit, onSaved, identity }) {
  const info = mon?.project || {};
  const loc = info.location || {};
  const scope = info.scope_of_work || [];
  const contacts = info.client_contacts || [];
  const site = info.site_contact || {};
  const docs = info.project_documents || [];
  const hasCoords = loc.latitude != null && loc.longitude != null;
  // 2026-06-20 (user request): input koordinat diganti Google Plus Code — Google Maps
  // me-resolve plus code secara native, jadi link peta cukup memakai kode tsb.
  const openMapsHref = loc.plus_code
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.plus_code)}`
    : hasCoords
      ? `https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`
      : (loc.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address)}` : null);
  // 2026-06-20 (user request): peta lokasi juga tampil di tab Data Proyek (seperti Ringkasan).
  const plusCode = (loc.plus_code || "").trim();
  const mapSrc = plusCode
    ? `https://www.google.com/maps?q=${encodeURIComponent(plusCode)}&output=embed`
    : hasCoords
      ? `https://www.openstreetmap.org/export/embed.html?bbox=${loc.longitude - 0.02},${loc.latitude - 0.02},${loc.longitude + 0.02},${loc.latitude + 0.02}&layer=mapnik&marker=${loc.latitude},${loc.longitude}`
      : null;

  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [picOptions, setPicOptions] = useState([]);
  const [financePicOptions, setFinancePicOptions] = useState([]);

  const startEdit = () => {
    const src = project || {};
    const l = src.location || loc || {};
    setForm({
      project_name: src.project_name || info.project_name || "",
      client: src.client || info.client || "",
      contract_number: src.contract_number || info.contract_number || "",
      contract_value: src.contract_value != null ? String(src.contract_value) : "",
      start_date: toDateInput(src.start_date || info.start_date),
      end_date: toDateInput(src.end_date || info.end_date),
      pic_name: src.pic_name || info.pic_name || "",
      pic_finance_name: src.pic_finance_name || info.pic_finance_name || "",
      loc_address: l.address || "",
      loc_province: l.province || src.province || "",
      loc_city: l.city || src.city || "",
      loc_lat: l.latitude != null ? String(l.latitude) : "",
      loc_lng: l.longitude != null ? String(l.longitude) : "",
      plus_code: l.plus_code || "",
      scope_of_work: (src.scope_of_work || scope || []).map((s) => ({ description: s.description || "", category: s.category || "", weight_pct: s.weight_pct != null ? String(s.weight_pct) : "" })),
      client_contacts: (src.client_contacts || contacts || []).map((c) => ({ name: c.name || "", role: c.role || c.position || "", phone: c.phone || "", email: c.email || "" })),
      site_name: (src.site_contact || site || {}).name || "",
      site_phone: (src.site_contact || site || {}).phone || "",
      site_email: (src.site_contact || site || {}).email || "",
      project_documents: (src.project_documents || docs || []).map((d) => ({ name: d.name || "", type: d.type || "", version: d.version || "", date: (d.date || "").slice(0, 10), status: d.status || "", url: d.url || "" })),
      is_ppn_exempt: !!src.is_ppn_exempt,
      retention_pct: src.retention_pct != null ? Number(src.retention_pct) : 0,
      retention_period_days: src.retention_period_days != null ? Number(src.retention_period_days) : 0,
      retention_type: src.retention_type || "inside",
      vo_aggregation: src.vo_aggregation || "separate",
    });
    setEditing(true);
    // Opsi client (dari project lain) & PIC (staff divisi Project saja).
    api.get("/active-projects").then(({ data }) => {
      const uniq = Array.from(new Set((data || []).filter((p) => p.project_type === "new_project").map((p) => (p.client || "").trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
      setClientOptions(uniq);
    }).catch(() => {});
    api.get("/users").then(({ data }) => {
      const active = (data || []).filter((u) => u.is_active !== false && !u.disabled_at);
      const staff = active.filter((u) => u.division === "Project").map((u) => u.name).filter(Boolean).sort((a, b) => a.localeCompare(b));
      setPicOptions(staff);
      // PIC Keuangan (2026-06-20): bebas — siapa saja yang ditunjuk, semua user aktif.
      setFinancePicOptions(active.map((u) => u.name).filter(Boolean).sort((a, b) => a.localeCompare(b)));
    }).catch(() => {});
  };

  const handleEdit = () => {
    // VO/WO punya field berbeda — tetap pakai dialog lama via parent.
    if (project?.project_type && project.project_type !== "new_project") { onEdit?.(); return; }
    startEdit();
  };

  const setRow = (key, i, field, value) => setForm((f) => ({ ...f, [key]: f[key].map((r, idx) => (idx === i ? { ...r, [field]: value } : r)) }));
  const addRow = (key, blank) => setForm((f) => ({ ...f, [key]: [...f[key], blank] }));
  const delRow = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.project_name.trim()) { toast.error("Nama proyek wajib diisi."); return; }
    if (!form.client.trim()) { toast.error("Client wajib diisi."); return; }
    const lat = num(form.loc_lat), lng = num(form.loc_lng);
    if (lat != null && (lat < -90 || lat > 90)) { toast.error("Latitude harus antara -90 dan 90."); return; }
    if (lng != null && (lng < -180 || lng > 180)) { toast.error("Longitude harus antara -180 dan 180."); return; }
    setBusy(true);
    try {
      const payload = {
        project_name: form.project_name.trim(),
        client: form.client.trim(),
        contract_number: form.contract_number.trim() || null,
        contract_value: form.contract_value === "" ? null : parseFloat(form.contract_value),
        start_date: toIso(form.start_date),
        end_date: toIso(form.end_date),
        pic_name: (form.pic_name || "").trim() || null,
        pic_finance_name: (form.pic_finance_name || "").trim() || null,
        province: form.loc_province || null,
        city: form.loc_city || null,
        location: {
          address: form.loc_address.trim() || null,
          province: form.loc_province || null,
          city: form.loc_city || null,
          // 2026-06-20: plus code jadi kolom utama; lat/lng lama dipertahankan apa adanya.
          plus_code: form.plus_code.trim() || null,
          latitude: lat,
          longitude: lng,
        },
        scope_of_work: form.scope_of_work.filter((s) => s.description.trim()).map((s) => ({ description: s.description.trim(), category: s.category.trim() || null, weight_pct: num(s.weight_pct) })),
        client_contacts: form.client_contacts.filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), role: c.role.trim() || null, phone: c.phone.trim() || null, email: c.email.trim() || null })),
        site_contact: (form.site_name.trim() || form.site_phone.trim() || form.site_email.trim())
          ? { name: form.site_name.trim() || null, phone: form.site_phone.trim() || null, email: form.site_email.trim() || null }
          : null,
        project_documents: form.project_documents.filter((d) => d.name.trim()).map((d) => ({ name: d.name.trim(), type: d.type.trim() || null, version: d.version.trim() || null, date: d.date || null, status: d.status.trim() || null, url: d.url.trim() || null })),
        is_ppn_exempt: !!form.is_ppn_exempt,
        retention_pct: Number(form.retention_pct) || 0,
        retention_period_days: Number(form.retention_period_days) || 0,
        retention_type: form.retention_type || "inside",
        vo_aggregation: form.vo_aggregation || "separate",
      };
      await api.patch(`/active-projects/${project.id}`, payload);
      // Auto-cascade PPN ke sub-projects saat parent ubah status Non-PPN (mirror dialog lama).
      if (!!form.is_ppn_exempt !== !!project.is_ppn_exempt) {
        try {
          const { data: casc } = await api.post(`/active-projects/${project.id}/cascade-ppn-exempt`);
          if (casc?.updated > 0) toast.success(`${casc.updated} sub-project auto-sync → ${casc.is_ppn_exempt ? "Non-PPN" : "kena PPN 11%"}`);
        } catch { toast.warning("Data tersimpan, tapi auto-cascade PPN ke sub-project gagal."); }
      }
      toast.success("Data project tersimpan.");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal menyimpan data project.");
    } finally {
      setBusy(false);
    }
  };

  const actionButtons = editing && (
    <div className="flex items-center gap-2">
      <button type="button" data-testid="dp-btn-cancel" onClick={() => setEditing(false)} disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
        <X size={12} /> Batal
      </button>
      <button type="button" data-testid="dp-btn-save" onClick={save} disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF5A1F] hover:bg-[#e04a13] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} {busy ? "Menyimpan…" : "Simpan Perubahan"}
      </button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="data-proyek-tab">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-slate-500">
          {editing
            ? <span className="text-[#FF5A1F] font-semibold" data-testid="data-proyek-edit-mode">Mode edit aktif — ubah data langsung di halaman ini, lalu Simpan Perubahan.</span>
            : "Klik Edit Data Project untuk mengubah semua data langsung di halaman ini (tanpa popup)."}
        </div>
        {canEdit && !editing && (
          <button type="button" data-testid="btn-edit-data-proyek" onClick={handleEdit}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold">
            <Pencil size={12} /> Edit Data Project
          </button>
        )}
        {actionButtons}
      </div>

      {/* 2026-06-20 (user request): identitas (kontrak/PO, nilai kontrak, tanggal,
          client, PIC) bersumber dari proyek induk — konflik anak≠induk ditampilkan
          sebagai peringatan. Data historis tidak ditimpa. */}
      {identity?.isInherited && (
        <div className={`border px-4 py-2.5 text-xs flex items-start gap-2 ${identity.conflicts.length ? "border-amber-300 bg-amber-50 text-amber-800" : "border-sky-200 bg-sky-50 text-sky-800"}`} data-testid="dp-identity-note">
          {identity.conflicts.length ? <AlertTriangle size={14} className="shrink-0 mt-0.5" /> : <Building2 size={14} className="shrink-0 mt-0.5" />}
          <div>
            <div className="font-semibold">
              Identitas proyek bersumber dari induk: {identity.source?.project_name || "—"}. Nilai pada formulir ini adalah nilai lokal sub-proyek.
            </div>
            {identity.conflicts.length > 0 && (
              <div className="mt-1" data-testid="dp-identity-conflicts">
                Konflik terdeteksi: {identity.conflicts.map((c) => `${c.label} sub ("${c.childValue}") ≠ induk ("${c.parentValue}")`).join(" · ")}.
                Data historis tidak diubah otomatis — selaraskan manual bila perlu.
              </div>
            )}
          </div>
        </div>
      )}

      <Section icon={Building2} title="Informasi Project" testid="section-info-project">
        {editing ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <ERow label="Kode Proyek" testid="dp-edit-code"><div className="text-xs text-slate-500 py-1.5">{info.project_code || "—"}</div></ERow>
            <ERow label="Nama Proyek *" testid="dp-edit-name-wrap"><Input data-testid="dp-edit-name" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} className={inp} /></ERow>
            <ERow label="Client *" testid="dp-edit-client-wrap">
              <Input data-testid="dp-edit-client" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} list="dp-client-suggestions" autoComplete="off" className={inp} />
              <datalist id="dp-client-suggestions">{clientOptions.map((c) => <option key={c} value={c} />)}</datalist>
            </ERow>
            <ERow label="Nomor Kontrak / PO" testid="dp-edit-contract-number-wrap"><Input data-testid="dp-edit-contract-number" value={form.contract_number} onChange={(e) => setForm({ ...form, contract_number: e.target.value })} className={inp} /></ERow>
            <ERow label="Nilai Kontrak (IDR / DPP)" testid="dp-edit-contract-value-wrap">
              <Input data-testid="dp-edit-contract-value" type="number" min="0" step="1" value={form.contract_value} onChange={(e) => setForm({ ...form, contract_value: e.target.value })} className={`${inp} tabular-nums`} />
              {form.contract_value && <div className="text-[10px] text-slate-500 tabular-nums mt-0.5">{formatIDR(parseFloat(form.contract_value) || 0)}</div>}
            </ERow>
            <ERow label="Status" testid="dp-edit-status"><div className="text-xs text-slate-500 py-1.5">{info.status || "—"}</div></ERow>
            <ERow label="PIC (Divisi Project)" testid="dp-edit-pic-wrap">
              <select data-testid="dp-edit-pic" value={form.pic_name} onChange={(e) => setForm({ ...form, pic_name: e.target.value })}
                className="w-full border border-slate-300 px-2 h-8 text-xs bg-white rounded-none focus:outline-none focus:border-[#0B1D3A]">
                <option value="">— Belum ditentukan —</option>
                {form.pic_name && !picOptions.includes(form.pic_name) && <option value={form.pic_name}>{form.pic_name} (saat ini)</option>}
                {picOptions.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </ERow>
            <ERow label="PIC Keuangan (bebas — siapa saja yang ditunjuk)" testid="dp-edit-pic-finance-wrap">
              <select data-testid="dp-edit-pic-finance" value={form.pic_finance_name} onChange={(e) => setForm({ ...form, pic_finance_name: e.target.value })}
                className="w-full border border-slate-300 px-2 h-8 text-xs bg-white rounded-none focus:outline-none focus:border-[#0B1D3A]">
                <option value="">— Belum ditentukan —</option>
                {form.pic_finance_name && !financePicOptions.includes(form.pic_finance_name) && <option value={form.pic_finance_name}>{form.pic_finance_name} (saat ini)</option>}
                {financePicOptions.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </ERow>
            <ERow label="Tanggal Mulai" testid="dp-edit-start-wrap"><DateField testid="dp-edit-start" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} /></ERow>
            <ERow label="Tanggal Selesai" testid="dp-edit-end-wrap"><DateField testid="dp-edit-end" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} /></ERow>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Row label="Kode Proyek" value={info.project_code} testid="row-code" />
            <Row label="Nama Proyek" value={info.project_name} testid="row-name" />
            <Row label="Client" value={info.client} testid="row-client" />
            <Row label="Nomor Kontrak / PO" value={info.contract_number} testid="row-contract" />
            <Row label="Nilai Kontrak" value={Number(info.contract_value || 0) > 0 ? formatIDR(info.contract_value) : "—"} testid="row-value" />
            {/* 2026-06-20 (user request): agregasi nilai sub ditampilkan berlabel,
                TIDAK sebagai nilai kontrak induk. */}
            {Number(mon?.summary?.sub_value_sum || 0) > 0 && (
              <Row label={`Total Nilai ${project?.is_outlet ? "WO" : "VO"} (agregasi — bukan nilai kontrak induk)`} value={formatIDR(mon.summary.sub_value_sum)} testid="row-sub-value" />
            )}
            <Row label="Status" value={info.status} testid="row-status" />
            <Row label="PIC" value={info.pic_name} testid="row-pic" />
            <Row label="PIC Keuangan" value={info.pic_finance_name} testid="row-pic-finance" />
            <Row label="Tanggal Mulai" value={info.start_date ? fmtDate(info.start_date) : null} testid="row-start" />
            <Row label="Tanggal Selesai" value={info.end_date ? fmtDate(info.end_date) : null} testid="row-end" />
          </div>
        )}
      </Section>

      <Section icon={MapPin} title="Lokasi Project" testid="section-lokasi"
        right={openMapsHref && (
          <a href={openMapsHref} target="_blank" rel="noreferrer" data-testid="btn-buka-peta-data"
            className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-[10px] uppercase tracking-wider font-semibold">
            <ExternalLink size={11} /> Buka di Peta
          </a>
        )}>
        {editing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ERow label="Alamat" testid="dp-edit-address-wrap"><Input data-testid="dp-edit-address" value={form.loc_address} onChange={(e) => setForm({ ...form, loc_address: e.target.value })} className={inp} placeholder="Alamat lengkap lokasi project" /></ERow>
            <div className="grid grid-cols-2 gap-3">
              <ERow label="Provinsi" testid="dp-edit-province-wrap">
                <Combobox testid="dp-edit-province" value={form.loc_province} onChange={(v) => setForm({ ...form, loc_province: v, loc_city: form.loc_province === v ? form.loc_city : "" })} options={PROVINCES} placeholder="Pilih provinsi…" />
              </ERow>
              <ERow label="Kota / Kabupaten" testid="dp-edit-city-wrap">
                <Combobox testid="dp-edit-city" value={form.loc_city} onChange={(v) => setForm({ ...form, loc_city: v })} options={form.loc_province ? INDONESIA_REGIONS[form.loc_province] || [] : []} placeholder={form.loc_province ? "Pilih kota…" : "Pilih provinsi dulu"} disabled={!form.loc_province} />
              </ERow>
            </div>
            <ERow label="Google Plus Code" testid="dp-edit-pluscode-wrap">
              <Input data-testid="dp-edit-pluscode" value={form.plus_code} onChange={(e) => setForm({ ...form, plus_code: e.target.value })} className={inp} placeholder="cth: 6PFG57QM+Q9C  atau  57QM+Q9 Medan" />
              <div className="text-[10px] text-slate-400 mt-0.5">Salin kode plus dari Google Maps / Google Earth (klik-tahan titik lokasi → kode muncul di kartu info). Kolom ini menggantikan Latitude/Longitude.</div>
            </ERow>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Row label="Alamat" value={loc.address} testid="row-address" />
            <Row label="Provinsi / Kota" value={[loc.city, loc.province].filter(Boolean).join(", ") || null} testid="row-city" />
            <Row label="Google Plus Code" value={loc.plus_code || (hasCoords ? `${Number(loc.latitude).toFixed(6)}, ${Number(loc.longitude).toFixed(6)} (koordinat lama)` : null)} testid="row-plus-code" />
          </div>
        )}
        {/* 2026-06-20 (user request): peta lokasi seperti di tab Ringkasan. */}
        {!editing && (mapSrc ? (
          <iframe title="Peta Lokasi Project" src={mapSrc} className="w-full h-56 border border-slate-200 mt-3" data-testid="dp-lokasi-iframe" />
        ) : (
          <div className="h-20 border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-[11px] text-slate-400 text-center px-3 mt-3" data-testid="dp-lokasi-map-empty">
            Belum ada peta — isi Google Plus Code lewat tombol Edit.
          </div>
        ))}
      </Section>

      <Section icon={FileText} title="Ruang Lingkup Pekerjaan" testid="section-scope">
        {editing ? (
          <div className="space-y-2">
            {form.scope_of_work.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`dp-scope-row-${i}`}>
                <div className="col-span-6"><ERow label="Deskripsi Pekerjaan"><Input data-testid={`dp-scope-desc-${i}`} value={s.description} onChange={(e) => setRow("scope_of_work", i, "description", e.target.value)} className={inp} /></ERow></div>
                <div className="col-span-3"><ERow label="Jenis / Kategori"><Input data-testid={`dp-scope-cat-${i}`} value={s.category} onChange={(e) => setRow("scope_of_work", i, "category", e.target.value)} className={inp} /></ERow></div>
                <div className="col-span-2"><ERow label="Bobot (%)"><Input data-testid={`dp-scope-weight-${i}`} value={s.weight_pct} onChange={(e) => setRow("scope_of_work", i, "weight_pct", e.target.value.replace(/[^0-9.]/g, ""))} inputMode="numeric" className={`${inp} tabular-nums`} /></ERow></div>
                <button type="button" data-testid={`dp-scope-del-${i}`} onClick={() => delRow("scope_of_work", i)} className="col-span-1 h-8 flex items-center justify-center text-rose-500 hover:text-rose-700"><Trash2 size={14} /></button>
              </div>
            ))}
            <button type="button" data-testid="dp-scope-add" onClick={() => addRow("scope_of_work", { description: "", category: "", weight_pct: "" })}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-dashed border-slate-300 text-slate-600 hover:border-[#0B1D3A] hover:text-[#0B1D3A] text-[10px] uppercase tracking-wider font-semibold">
              <Plus size={11} /> Tambah Ruang Lingkup
            </button>
          </div>
        ) : scope.length === 0 ? (
          <div className="text-[11px] text-slate-400 text-center py-3" data-testid="scope-empty">Belum ada ruang lingkup pekerjaan yang tercatat.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">{["Deskripsi Pekerjaan", "Jenis / Kategori", "Bobot"].map((h) => <th key={h} className="px-2 py-1.5 overline text-slate-600">{h}</th>)}</tr>
            </thead>
            <tbody>
              {scope.map((s, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-2 py-1.5 text-[#0B1D3A]">{s.description || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-600">{s.category || "—"}</td>
                  <td className="px-2 py-1.5 tabular-nums text-slate-700">{s.weight_pct != null ? `${s.weight_pct}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section icon={Users} title="Kontak Project" testid="section-contacts">
        {editing ? (
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Client PIC</div>
              <div className="space-y-2">
                {form.client_contacts.map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`dp-contact-row-${i}`}>
                    <div className="col-span-4"><ERow label="Nama"><Input data-testid={`dp-contact-name-${i}`} value={c.name} onChange={(e) => setRow("client_contacts", i, "name", e.target.value)} className={inp} /></ERow></div>
                    <div className="col-span-3"><ERow label="Jabatan"><Input data-testid={`dp-contact-role-${i}`} value={c.role} onChange={(e) => setRow("client_contacts", i, "role", e.target.value)} className={inp} /></ERow></div>
                    <div className="col-span-2"><ERow label="Telepon"><Input data-testid={`dp-contact-phone-${i}`} value={c.phone} onChange={(e) => setRow("client_contacts", i, "phone", e.target.value)} className={inp} /></ERow></div>
                    <div className="col-span-2"><ERow label="Email"><Input data-testid={`dp-contact-email-${i}`} value={c.email} onChange={(e) => setRow("client_contacts", i, "email", e.target.value)} className={inp} /></ERow></div>
                    <button type="button" data-testid={`dp-contact-del-${i}`} onClick={() => delRow("client_contacts", i)} className="col-span-1 h-8 flex items-center justify-center text-rose-500 hover:text-rose-700"><Trash2 size={14} /></button>
                  </div>
                ))}
                <button type="button" data-testid="dp-contact-add" onClick={() => addRow("client_contacts", { name: "", role: "", phone: "", email: "" })}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-dashed border-slate-300 text-slate-600 hover:border-[#0B1D3A] hover:text-[#0B1D3A] text-[10px] uppercase tracking-wider font-semibold">
                  <Plus size={11} /> Tambah Kontak Client
                </button>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Site Contact</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <ERow label="Nama"><Input data-testid="dp-edit-site-name" value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} className={inp} /></ERow>
                <ERow label="Telepon"><Input data-testid="dp-edit-site-phone" value={form.site_phone} onChange={(e) => setForm({ ...form, site_phone: e.target.value })} className={inp} /></ERow>
                <ERow label="Email"><Input data-testid="dp-edit-site-email" value={form.site_email} onChange={(e) => setForm({ ...form, site_email: e.target.value })} className={inp} /></ERow>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Client PIC</div>
              {contacts.length === 0 ? (
                <div className="text-[11px] text-slate-400" data-testid="contacts-empty">Belum ada kontak client tercatat.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {contacts.map((c, i) => (
                    <div key={i} className="border border-slate-200 p-3" data-testid={`client-contact-${i}`}>
                      <div className="text-xs font-semibold text-[#0B1D3A]">{c.name || "—"}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{c.role || c.position || ""}</div>
                      {c.phone && <div className="text-[11px] text-slate-700 mt-1">📞 {c.phone}</div>}
                      {c.email && <div className="text-[11px] text-slate-700">✉️ {c.email}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Site Contact</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="site-contact">
                <Row label="Nama" value={site.name} testid="site-name" />
                <Row label="Telepon" value={site.phone} testid="site-phone" />
                <Row label="Email" value={site.email} testid="site-email" />
              </div>
            </div>
          </div>
        )}
      </Section>

      <Section icon={FileText} title="Dokumen Project" testid="section-documents">
        {editing ? (
          <div className="space-y-2">
            {form.project_documents.map((d, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`dp-doc-row-${i}`}>
                <div className="col-span-3"><ERow label="Nama Dokumen"><Input data-testid={`dp-doc-name-${i}`} value={d.name} onChange={(e) => setRow("project_documents", i, "name", e.target.value)} className={inp} /></ERow></div>
                <div className="col-span-2"><ERow label="Jenis"><Input data-testid={`dp-doc-type-${i}`} value={d.type} onChange={(e) => setRow("project_documents", i, "type", e.target.value)} className={inp} placeholder="Kontrak/SPH/PO…" /></ERow></div>
                <div className="col-span-1"><ERow label="Versi"><Input data-testid={`dp-doc-version-${i}`} value={d.version} onChange={(e) => setRow("project_documents", i, "version", e.target.value)} className={inp} /></ERow></div>
                <div className="col-span-2"><ERow label="Tanggal"><Input data-testid={`dp-doc-date-${i}`} type="date" value={d.date} onChange={(e) => setRow("project_documents", i, "date", e.target.value)} className={`${inp} tabular-nums`} /></ERow></div>
                <div className="col-span-1"><ERow label="Status"><Input data-testid={`dp-doc-status-${i}`} value={d.status} onChange={(e) => setRow("project_documents", i, "status", e.target.value)} className={inp} placeholder="Final" /></ERow></div>
                <div className="col-span-2"><ERow label="Tautan File"><Input data-testid={`dp-doc-url-${i}`} value={d.url} onChange={(e) => setRow("project_documents", i, "url", e.target.value)} className={inp} placeholder="https://…" /></ERow></div>
                <button type="button" data-testid={`dp-doc-del-${i}`} onClick={() => delRow("project_documents", i)} className="col-span-1 h-8 flex items-center justify-center text-rose-500 hover:text-rose-700"><Trash2 size={14} /></button>
              </div>
            ))}
            <button type="button" data-testid="dp-doc-add" onClick={() => addRow("project_documents", { name: "", type: "", version: "", date: "", status: "", url: "" })}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-dashed border-slate-300 text-slate-600 hover:border-[#0B1D3A] hover:text-[#0B1D3A] text-[10px] uppercase tracking-wider font-semibold">
              <Plus size={11} /> Tambah Dokumen
            </button>
          </div>
        ) : docs.length === 0 ? (
          <div className="text-[11px] text-slate-400 text-center py-3" data-testid="docs-empty">Belum ada dokumen project tercatat.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">{["Nama Dokumen", "Jenis", "Versi", "Tanggal", "Status", "Aksi"].map((h) => <th key={h} className="px-2 py-1.5 overline text-slate-600">{h}</th>)}</tr>
            </thead>
            <tbody>
              {docs.map((d, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-2 py-1.5 text-[#0B1D3A] font-medium">{d.name || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-600">{d.type || "—"}</td>
                  <td className="px-2 py-1.5 tabular-nums text-slate-700">{d.version || "—"}</td>
                  <td className="px-2 py-1.5 tabular-nums text-slate-500">{d.date ? fmtDate(d.date) : "—"}</td>
                  <td className="px-2 py-1.5"><span className="text-[9px] font-semibold uppercase tracking-wider border border-slate-300 bg-slate-50 text-slate-700 px-1.5 py-0.5">{d.status || "—"}</span></td>
                  <td className="px-2 py-1.5">
                    {d.url ? (
                      <a href={d.url} target="_blank" rel="noreferrer" data-testid={`doc-download-${i}`}
                        className="inline-flex items-center gap-1 text-[10px] text-[#0B1D3A] hover:text-[#FF5A1F] uppercase tracking-wider font-semibold">
                        <Download size={11} /> Lihat
                      </a>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Pengaturan kontrak & pajak — dipindah dari popup Edit Project lama (user request). */}
      {editing && (
        <Section icon={Settings2} title="Pengaturan Kontrak & Pajak" testid="section-contract-settings">
          <div className="space-y-4">
            <div>
              <label className="flex items-center gap-1.5 text-[10px] cursor-pointer select-none" data-testid="dp-edit-non-ppn-toggle">
                <input type="checkbox" checked={!!form.is_ppn_exempt} onChange={(e) => setForm({ ...form, is_ppn_exempt: e.target.checked })} className="h-3 w-3 accent-[#FF5A1F]" />
                <span className="uppercase tracking-wider font-semibold text-slate-600">Project Non-PPN (bebas PPN 11%) — akan meng-update semua VO/WO otomatis</span>
              </label>
              <PPNBreakdown value={form.contract_value} isExempt={!!form.is_ppn_exempt} testid="dp-edit-ppn-breakdown" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 mb-1">Retensi</div>
              <div className="inline-flex border border-slate-300 text-xs" data-testid="dp-edit-retention">
                {[{ v: 0, label: "None" }, { v: 5, label: "5%" }, { v: 10, label: "10%" }].map((r) => (
                  <button key={r.v} type="button" data-testid={`dp-edit-retention-${r.v}`} onClick={() => setForm({ ...form, retention_pct: r.v })}
                    className={`px-4 py-1.5 uppercase tracking-wider font-semibold ${Number(form.retention_pct) === r.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                    {r.label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">Berlaku untuk penagihan berikutnya. Penagihan lama memakai snapshot retensi aslinya.</p>
            </div>
            {Number(form.retention_pct) > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="overline text-[9px] text-slate-500 mb-1">Jenis Retensi</div>
                  <div className="inline-flex border border-slate-300 text-xs" data-testid="dp-edit-retention-type">
                    {[{ v: "inside", label: "Ditahan per penagihan" }, { v: "outside", label: "Ditahan pada akhir penagihan" }].map((r) => (
                      <button key={r.v} type="button" data-testid={`dp-edit-retention-type-${r.v}`} onClick={() => setForm({ ...form, retention_type: r.v })}
                        className={`px-3 py-1.5 uppercase tracking-wider font-semibold ${(form.retention_type || "inside") === r.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
                <ERow label="Masa Retensi (hari)" testid="dp-edit-retention-period-wrap">
                  <Input data-testid="dp-edit-retention-period-days" type="number" min="0" max="3650" step="1" value={form.retention_period_days ?? 0} onChange={(e) => setForm({ ...form, retention_period_days: e.target.value })} className={`${inp} tabular-nums`} />
                </ERow>
              </div>
            )}
            <div>
              <div className="overline text-[9px] text-slate-500 mb-1">Perhitungan VO</div>
              <div className="inline-flex border border-slate-300 text-xs" data-testid="dp-edit-vo-aggregation">
                {[{ v: "separate", label: "VO Dipisah" }, { v: "combined", label: "VO Digabung" }].map((r) => (
                  <button key={r.v} type="button" data-testid={`dp-edit-vo-agg-${r.v}`} onClick={() => setForm({ ...form, vo_aggregation: r.v })}
                    className={`px-4 py-1.5 uppercase tracking-wider font-semibold ${(form.vo_aggregation || "separate") === r.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Section>
      )}

      {editing && (
        <div className="sticky bottom-0 bg-white border border-slate-200 px-4 py-3 flex items-center justify-end gap-2" data-testid="dp-edit-actionbar">
          <button type="button" data-testid="dp-btn-cancel-bottom" onClick={() => setEditing(false)} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
            <X size={12} /> Batal
          </button>
          <button type="button" data-testid="dp-btn-save-bottom" onClick={save} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF5A1F] hover:bg-[#e04a13] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} {busy ? "Menyimpan…" : "Simpan Perubahan"}
          </button>
        </div>
      )}
    </div>
  );
}
